import * as XLSX from "xlsx";

const COLUMN_COUNT = 9;

const palette = {
  navy: "1E3A8A",
  blue: "2563EB",
  paleBlue: "DBEAFE",
  lighterBlue: "EFF6FF",
  slate: "475569",
  paleGray: "F1F5F9",
  border: "CBD5E1",
  white: "FFFFFF",
  green: "DCFCE7",
  greenText: "166534",
  amber: "FEF3C7",
  amberText: "92400E",
};

const blankRow = () => Array(COLUMN_COUNT).fill("");

const resultPercentage = (result) => {
  if (
    result.resultType !== "marks" ||
    result.isAbsent ||
    result.marksObtained == null ||
    result.maximumMarks == null ||
    Number(result.maximumMarks) <= 0
  ) {
    return "";
  }
  return Number(result.marksObtained) / Number(result.maximumMarks);
};

const statusStyle = (status) => {
  if (status === "locked") {
    return { fill: palette.green, font: palette.greenText };
  }
  if (status === "submitted") {
    return { fill: palette.amber, font: palette.amberText };
  }
  return { fill: palette.paleGray, font: palette.slate };
};

const formatVerifiedAt = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const buildEducationReportWorkbook = (report) => {
  const rows = [];
  const merges = [];
  const styledRows = [];
  const percentageRows = [];

  const addRow = (values = [], style = null) => {
    const row = [...values, ...blankRow()].slice(0, COLUMN_COUNT);
    const rowIndex = rows.push(row) - 1;
    if (style) styledRows.push({ rowIndex, style });
    return rowIndex;
  };

  const addMergedRow = (value, style, startColumn = 0, endColumn = COLUMN_COUNT - 1) => {
    const values = blankRow();
    values[startColumn] = value;
    const rowIndex = addRow(values, style);
    merges.push({ s: { r: rowIndex, c: startColumn }, e: { r: rowIndex, c: endColumn } });
    return rowIndex;
  };

  addMergedRow("EDUCATION REPORT", "reportTitle");
  addRow(["Name", `${report.student.firstName} ${report.student.lastName}`.trim()], "identity");
  addRow(["College", report.profile?.collegeName || "—"], "identity");
  addRow(["University", report.profile?.universityName || "—"], "identity");
  addRow();

  for (const year of report.academicYears || []) {
    addMergedRow(`ACADEMIC YEAR: ${year.label}`, "academicYear");

    for (const semester of year.semesters || []) {
      addMergedRow(`SEMESTER: ${semester.name}`, "semester");
      if (semester.sgpa != null || semester.cgpa != null) {
        addRow([
          "SGPA",
          semester.sgpa ?? "—",
          "",
          "CGPA",
          semester.cgpa ?? "—",
        ], "gpa");
      }

      addMergedRow("SUBJECT LIST", "section");
      addRow(["Subject", "Code", "Category"], "subjectHeader");
      if ((semester.subjects || []).length === 0) {
        addMergedRow("No semester subjects recorded", "empty", 0, 2);
      } else {
        for (const subject of semester.subjects) {
          addRow([
            subject.name,
            subject.code || "—",
            subject.isBacklog ? "Backlog" : "Regular",
          ], "subjectRow");
        }
      }
      addRow();

      const subjectMap = new Map(
        (semester.selectableSubjects || semester.subjects || []).map((subject) => [
          Number(subject.id),
          subject,
        ])
      );

      for (const exam of semester.exams || []) {
        const typeLabel = exam.type === "end_semester" ? "End Semester" : "Internal / Midsem";
        const examHeaderRow = addRow([
          `EXAM: ${exam.name}`,
          "",
          "",
          "",
          "",
          "",
          `TYPE: ${typeLabel}`,
          `STATUS: ${exam.status?.toUpperCase() || "DRAFT"}`,
        ], "examTitle");
        merges.push({ s: { r: examHeaderRow, c: 0 }, e: { r: examHeaderRow, c: 5 } });
        merges.push({ s: { r: examHeaderRow, c: 7 }, e: { r: examHeaderRow, c: 8 } });
        styledRows.push({ rowIndex: examHeaderRow, style: `status:${exam.status || "draft"}`, startColumn: 7 });

        if (exam.verifiedBy || exam.verifiedAt || exam.verificationNotes) {
          addRow([
            "Verified by",
            exam.verifiedBy || "—",
            "",
            "Verified at",
            formatVerifiedAt(exam.verifiedAt),
            "",
            "Notes",
            exam.verificationNotes || "—",
          ], "verification");
        }

        addRow([
          "Subject",
          "Code",
          "Result Type",
          "Obtained",
          "Maximum",
          "Grade",
          "Absent",
          "Failed / Backlog",
          "%",
        ], "examTableHeader");

        if ((exam.results || []).length === 0) {
          addMergedRow("No results recorded", "empty");
        } else {
          for (const result of exam.results) {
            const subject = subjectMap.get(Number(result.subjectId)) || {};
            const isMarks = result.resultType === "marks";
            const rowIndex = addRow([
              subject.name || `Subject ${result.subjectId}`,
              subject.code || "—",
              isMarks ? "Marks" : "Grade",
              isMarks && !result.isAbsent && result.marksObtained != null
                ? Number(result.marksObtained)
                : "—",
              isMarks && result.maximumMarks != null ? Number(result.maximumMarks) : "—",
              !isMarks && !result.isAbsent ? result.grade || "—" : "—",
              result.isAbsent ? "Yes" : "No",
              result.isFailed ? "Yes" : "No",
              resultPercentage(result),
            ], result.isFailed || result.isAbsent ? "exceptionResult" : "resultRow");
            if (resultPercentage(result) !== "") percentageRows.push(rowIndex);
          }
        }
        addRow();
      }

      if ((semester.exams || []).length === 0) {
        addMergedRow("No exams recorded", "empty");
        addRow();
      }
      addRow();
    }
  }

  if (!(report.academicYears || []).length) {
    addMergedRow("No academic years recorded", "empty");
  }

  const worksheet = XLSX.utils.aoa_to_sheet(rows, { cellDates: true });
  worksheet["!merges"] = merges;
  worksheet["!cols"] = [
    { wch: 36 },
    { wch: 20 },
    { wch: 18 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 24 },
    { wch: 34 },
    { wch: 12 },
  ];
  worksheet["!rows"] = rows.map((_, index) => ({
    hpt: index === 0 ? 28 : rows[index].every((value) => value === "") ? 8 : 20,
  }));
  worksheet["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", state: "frozen" };
  worksheet["!margins"] = { left: 0.3, right: 0.3, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 };

  const baseBorder = {
    top: { style: "thin", color: { rgb: palette.border } },
    bottom: { style: "thin", color: { rgb: palette.border } },
    left: { style: "thin", color: { rgb: palette.border } },
    right: { style: "thin", color: { rgb: palette.border } },
  };
  const styles = {
    reportTitle: { fill: { fgColor: { rgb: palette.navy } }, font: { bold: true, color: { rgb: palette.white }, sz: 18 }, alignment: { horizontal: "center", vertical: "center" } },
    identity: { font: { sz: 11 }, alignment: { vertical: "center" } },
    academicYear: { fill: { fgColor: { rgb: palette.blue } }, font: { bold: true, color: { rgb: palette.white }, sz: 14 }, alignment: { vertical: "center" } },
    semester: { fill: { fgColor: { rgb: palette.paleBlue } }, font: { bold: true, color: { rgb: palette.navy }, sz: 12 }, alignment: { vertical: "center" } },
    gpa: { fill: { fgColor: { rgb: palette.lighterBlue } }, font: { bold: true, color: { rgb: palette.slate } } },
    section: { font: { bold: true, color: { rgb: palette.navy } }, alignment: { vertical: "center" } },
    subjectHeader: { fill: { fgColor: { rgb: palette.paleGray } }, font: { bold: true, color: { rgb: palette.slate } }, border: baseBorder },
    subjectRow: { border: baseBorder },
    examTitle: { fill: { fgColor: { rgb: palette.slate } }, font: { bold: true, color: { rgb: palette.white }, sz: 11 }, alignment: { vertical: "center" } },
    verification: { fill: { fgColor: { rgb: palette.green } }, font: { color: { rgb: palette.greenText } }, alignment: { wrapText: true, vertical: "center" } },
    examTableHeader: { fill: { fgColor: { rgb: palette.paleGray } }, font: { bold: true, color: { rgb: palette.slate } }, border: baseBorder, alignment: { wrapText: true, vertical: "center" } },
    resultRow: { border: baseBorder, alignment: { vertical: "center" } },
    exceptionResult: { fill: { fgColor: { rgb: "FEF2F2" } }, border: baseBorder, alignment: { vertical: "center" } },
    empty: { font: { italic: true, color: { rgb: "64748B" } }, alignment: { vertical: "center" } },
  };

  for (const { rowIndex, style, startColumn = 0 } of styledRows) {
    if (style.startsWith("status:")) {
      const status = statusStyle(style.slice("status:".length));
      for (let column = startColumn; column < COLUMN_COUNT; column += 1) {
        const address = XLSX.utils.encode_cell({ r: rowIndex, c: column });
        if (!worksheet[address]) worksheet[address] = { t: "s", v: "" };
        worksheet[address].s = {
          fill: { fgColor: { rgb: status.fill } },
          font: { bold: true, color: { rgb: status.font } },
          alignment: { horizontal: "center", vertical: "center" },
        };
      }
      continue;
    }
    const rowStyle = styles[style];
    if (!rowStyle) continue;
    const lastColumn = ["subjectHeader", "subjectRow"].includes(style) ? 2 : COLUMN_COUNT - 1;
    for (let column = startColumn; column <= lastColumn; column += 1) {
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: column });
      if (!worksheet[address]) worksheet[address] = { t: "s", v: "" };
      worksheet[address].s = rowStyle;
    }
  }

  for (const rowIndex of percentageRows) {
    const address = XLSX.utils.encode_cell({ r: rowIndex, c: 8 });
    worksheet[address].z = "0.0%";
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Education Report");
  return workbook;
};

export const exportEducationReport = (report) => {
  const workbook = buildEducationReportWorkbook(report);
  const studentName = `${report.student.firstName}_${report.student.lastName}`
    .replace(/[^a-z0-9_-]+/gi, "_")
    .replace(/^_+|_+$/g, "");
  XLSX.writeFile(workbook, `Education_Report_${studentName || report.student.id}.xlsx`, {
    cellStyles: true,
  });
};
